/** The identity app's routes other apps link to. */
export const IDENTITY_PATHS = {
  signIn: '/sign-in',
  register: '/register',
  /** Signs out of Firebase and the words-happen session, then shows sign-in carrying return_to. */
  signOut: '/sign-out',
  /** Requests a password reset email. The address is never in the URL: the player types it, or it comes from history state. */
  forgotPassword: '/forgot-password',
} as const;

export type IdentityPath = (typeof IDENTITY_PATHS)[keyof typeof IDENTITY_PATHS];

/**
 * Query parameter holding where to send the player once the identity flow completes (sign-in, registration,
 * sign-out, password reset). It is the only redirect parameter across the apps: there is no redirect_to, back_to or similar. The
 * receiving side always validates it with resolveReturnUrl.
 */
export const RETURN_TO_PARAM = 'return_to';

/** Query parameter naming the social provider the player picked before reaching the identity app. */
export const PROVIDER_PARAM = 'provider';

export interface IdentityAuthUrlOptions {
  /** Where the identity app sends the player back to; it is validated there. */
  returnTo: string;
  provider?: string;
}

/** A link to an identity app route, e.g. https://id.<domain>/sign-in?provider=google&return_to=<encoded URL>. */
export const identityAuthUrl = (
  identityOrigin: string,
  path: IdentityPath,
  { returnTo, provider }: IdentityAuthUrlOptions,
): string => {
  const url = new URL(path, identityOrigin);
  if (provider) {
    url.searchParams.set(PROVIDER_PARAM, provider);
  }
  url.searchParams.set(RETURN_TO_PARAM, returnTo);
  return url.href;
};
